import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { Navigation } from '@squareup/dex-ui';
import { Box } from '@squareup/dex-ui-shared-base';
import {
  mobileNavHelperStyles,
  NavigationMobileToggleProps,
} from '@squareup/dex-ui-shared-mobile-nav';
import clsx from 'clsx';
import React, { FunctionComponent, ReactNode, useEffect, useRef } from 'react';

import styles from './reference-sticky-navigation.module.css';

interface ReferenceStickyNavigationProps {
  children: ReactNode;
  testId?: string;
}

const STICKIED_ATTR = 'is-stickied';

/**
 * When a sticky element is in its "sticky" position,
 * add the attribute `is-stickied` to it.
 * The `top` of the ftoc is `40px`, so to clip
 * the top of the viewport by 1px we need a root
 * margin of -41px.
 * The bottom margin is set to be large so that it never
 * intersects, ensuring the `sticky` detector only works when
 * scrolling up. One day we might have a real `:stuck` pseudoselector
 * @param {*} e Element that can become sticky
 */
const addAttrWhenStickied = (element: HTMLElement) => {
  const observer = new IntersectionObserver(
    ([e]) => e?.target.classList.toggle(STICKIED_ATTR, e.intersectionRatio < 1),
    {
      threshold: [1],
      rootMargin: '-41px 0px 10000px 0px',
    }
  );

  observer.observe(element);
};

/**
 * This sticky nav works almost primarily with CSS, but requires some JS
 * logic when the user scrolls.
 *
 * Essentially, the main goal of this nav is to be sticky, and to dynamically update
 * the max-height of the scrollable element as the user scrolls to the sticky position.
 *
 * The reason we require so much logic is because, when an element is sticky with a
 * scrollable element inside, then the period before the element is "stuck" is a period where
 * the scrollable list can actually grow. So we must listen to the scroll, and grow accordingly.
 */
const ReferenceStickyNavigation: FunctionComponent<
  ReferenceStickyNavigationProps &
    NullableClassName &
    NavigationMobileToggleProps
> = ({ children, testId, className, ...rest }) => {
  const containerRef = useRef<HTMLElement | null>(null);
  const scrollableRef = useRef<HTMLUListElement>();
  const navDefaultMaxHeight = useRef<number>();

  const isMobileViewport = useRef<boolean>(false);

  useEffect(() => {
    /**
     * On scroll, we must do the following:
     * 1. If we have hit the "sticky" point of the element,
     * default to the max-height set in CSS and remove our inline 'max-height'
     * 2. Otherwise, we're at the point before the sticky. Take the initial
     * max height, and increase it by the current scroll
     */
    function onScroll() {
      if (
        isMobileViewport.current ||
        !scrollableRef.current ||
        !containerRef.current ||
        !navDefaultMaxHeight.current
      ) {
        return;
      }

      const scrollTop = document.documentElement.scrollTop;
      if (
        containerRef.current.classList.contains(STICKIED_ATTR) ||
        scrollTop >= Number.parseInt(containerRef.current.style.top, 10)
      ) {
        scrollableRef.current.style.removeProperty('max-height');
        return;
      }

      const currentMaxHeight = navDefaultMaxHeight.current;
      scrollableRef.current.style.maxHeight = `${
        currentMaxHeight + scrollTop
      }px`;
    }

    function onMobileViewport(e: MediaQueryListEvent) {
      isMobileViewport.current = e.matches;
      if (scrollableRef.current) {
        if (isMobileViewport.current) {
          scrollableRef.current.style.removeProperty('max-height');
        } else {
          // When switching back to desktop viewport, get the current default
          // max height before scrolling
          navDefaultMaxHeight.current = Number.parseInt(
            getComputedStyle(scrollableRef.current).maxHeight,
            10
          );
        }
      }
    }

    if (containerRef.current) {
      addAttrWhenStickied(containerRef.current);

      // We query for the ul element to make this HOC more ergonomic
      // Could instead pass as a ref in props in the future but this feels
      // a bit better
      scrollableRef.current =
        containerRef.current.querySelector<HTMLUListElement>('ul') || undefined;

      if (scrollableRef.current) {
        // Save the current max height before scolling. We use this to calculate
        // how much larger the actual nav will be before being "stuck"
        navDefaultMaxHeight.current = Number.parseInt(
          getComputedStyle(scrollableRef.current).maxHeight,
          10
        );
      }
    }

    // We have to stop the sticky nav logic in mobile
    // views. Unfortunately, that means requiring a matchMedia
    // rather than CSS.
    // TODO: Make some shared match media hooks in the same place
    // as where the breakpoints are defined
    const isMobileMatchMedia = window.matchMedia(
      'only screen and (max-width:767px)'
    );

    isMobileMatchMedia.addEventListener('change', onMobileViewport);
    document.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      isMobileMatchMedia.removeEventListener('change', onMobileViewport);
      document.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <Navigation
      testId={testId}
      className={clsx(
        styles.nav,
        mobileNavHelperStyles.vars,
        mobileNavHelperStyles.navigation,
        rest.isMobileNavOpen && mobileNavHelperStyles['mobile-nav-open'],
        className
      )}
      {...rest}
    >
      <Box
        data-testid="reference-sticky-container"
        ref={containerRef}
        className={styles.container}
      >
        {children}
      </Box>
    </Navigation>
  );
};

export { ReferenceStickyNavigation };
